import React, { useState } from "react";
import { Button } from "../ui/button";
import { Pencil } from "lucide-react";

interface Props {
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  paymentInfo: string;
  onPaymentInfoChange: (v: string) => void;
  onSubmit: () => void;
  loading?: boolean;
}

const PaymentConfirmation: React.FC<Props> = ({
  checked,
  onCheckedChange,
  paymentInfo,
  onPaymentInfoChange,
  onSubmit,
  loading = false,
}) => {
  const [editing, setEditing] = useState(false);

  const hasValue = paymentInfo.trim() !== "";
  const canShowReadView = hasValue && !editing && checked;

  const handleSave = () => {
    if (!paymentInfo.trim()) return;
    onSubmit();
    setEditing(false);
  };

  const handleEditClick = () => {
    setEditing(true);
  };

  const handleCheckboxClick = () => {
    if (!checked) {
      onCheckedChange(true);
      setEditing(true); // Immediately allow typing
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
      {/* Checkbox Row */}
      <div className="flex items-center gap-3 mb-4">
        <input
          type="checkbox"
          checked={checked}
          onChange={() => { }} // Prevent native change
          onClick={handleCheckboxClick}
          className="w-4 h-4 cursor-pointer accent-red-600"
          disabled={checked && !editing}
        />
        <label className="text-sm font-semibold text-red-600 cursor-pointer select-none">
          {checked && canShowReadView
            ? "✓ Payment confirmed"
            : "I have completed the payment"
          }
        </label>
      </div>

      {checked && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700 mb-1">
              UPI ID or Mobile Number
            </label>

            {canShowReadView ? (
              <div className="border rounded-md px-3 py-2 text-sm bg-gray-50 text-gray-800 flex items-center justify-between min-h-[42px]">
                <span className="truncate font-mono font-medium">{paymentInfo}</span>
              </div>
            ) : (
              <input
                type="text"
                placeholder="Ex: myname@upi / 9876543210"
                value={paymentInfo}
                onChange={(e) => onPaymentInfoChange(e.target.value)}
                autoFocus={!hasValue}
                maxLength={100}
                className="
                  border px-3 py-2 rounded-md text-sm outline-none font-mono
                  focus:ring-2 focus:ring-primary-2 focus:border-primary-2
                  transition-all duration-200
                  bg-white
                "
              />
            )}

            <p className="text-xs text-gray-500 mt-2 leading-tight">
              Enter the UPI ID or phone number from which you made the payment so it can be verified.
            </p>
          </div>

          <div className="flex items-end md:justify-end gap-2">
            {canShowReadView ? (
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={handleEditClick}
                className="flex items-center gap-1 h-10 px-4"
              >
                <Pencil size={16} />
                <span>Edit details</span>
              </Button>
            ) : (
              <Button
                disabled={!paymentInfo.trim() || loading}
                onClick={handleSave}
                type="button"
                className="bg-teal-700 hover:bg-teal-800 text-white font-medium rounded-md shadow-md h-10 px-6 min-w-[140px] transition-all duration-200"
              >
                {loading ? (
                  "Submitting..."
                ) : hasValue ? (
                  "Save changes"
                ) : (
                  "Submit payment details"
                )}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentConfirmation;
